/**
 * 일요일 출석 정리 + 수동 부여 화면
 * - 월 선택
 * - 월의 모든 일요일별로 출석 학생 목록 (시간 단위)
 * - 일요일별 "수동 부여" 버튼
 */
import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../../../infrastructure/api';

interface StudentSundayRecord {
  date: string;       // YYYY-MM-DD
  minutes: number;
  hours: number;
  first_entry?: string | null;
  last_exit?: string | null;
}

interface StudentAttendance {
  student_id: string;
  name: string;
  total_minutes: number;
  sundays: StudentSundayRecord[];
}

interface SundayAttendanceData {
  year_month: string;
  sundays: string[];           // YYYY-MM-DD 리스트
  students: StudentAttendance[];
}

const SundayAttendanceView: React.FC = () => {
  const now = new Date();
  const [yearMonth, setYearMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [data, setData] = useState<SundayAttendanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [awarding, setAwarding] = useState<string | null>(null); // 부여 중인 일요일
  const [confirming, setConfirming] = useState<string | null>(null); // 확인 대기 중인 일요일
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/v1/owner/sunday-merit/sunday-attendance?year_month=${yearMonth}`);
      setData(res.data);
    } catch (e: any) {
      console.error('일요일 출석 로드 실패:', e);
      setResult({ ok: false, text: e?.response?.data?.detail || '조회 실패' });
    } finally {
      setLoading(false);
    }
  }, [yearMonth]);

  useEffect(() => { load(); }, [load]);

  const changeMonth = (delta: number) => {
    const [y, m] = yearMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setYearMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const handleManualAward = async (sundayDate: string) => {
    setConfirming(null);
    setAwarding(sundayDate);
    setResult(null);
    try {
      const res = await apiClient.post('/v1/owner/sunday-merit/award', {
        target_date: sundayDate,
      });
      const r = res.data?.result || {};
      setResult({
        ok: true,
        text: `${sundayDate} 부여 완료 — 처리 ${r.processed ?? 0}명 / 부여 ${r.awarded ?? 0}명 / 중복 스킵 ${r.skipped_already ?? 0}명 / 한도 초과 스킵 ${r.skipped_max ?? 0}명`,
      });
      load();
    } catch (e: any) {
      setResult({ ok: false, text: e?.response?.data?.detail || '부여 실패' });
    } finally {
      setAwarding(null);
    }
  };

  const fmtTime = (iso?: string | null) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return '-'; }
  };

  // 모달(명단) 상태
  const [modalSunday, setModalSunday] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // 일요일별 출석 학생 명단 (이름 + 출석 기록)
  const attendeesOf = (sunday: string): Array<{ student_id: string; name: string } & StudentSundayRecord> => {
    if (!data) return [];
    const list: Array<{ student_id: string; name: string } & StudentSundayRecord> = [];
    for (const st of data.students) {
      const rec = st.sundays.find(r => r.date === sunday);
      if (rec) list.push({ student_id: st.student_id, name: st.name, ...rec });
    }
    return list;
  };

  const countOf = (sunday: string): number => attendeesOf(sunday).length;

  // 모달 학생 목록(검색 필터)
  const modalAttendees = modalSunday
    ? attendeesOf(modalSunday).filter(a => !search || a.name.includes(search))
    : [];

  return (
    <div className="bg-white rounded-lg border">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="font-medium text-gray-900 flex items-center gap-2">
          📅 일요일 출석 정리 (수동 부여 가능)
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={() => changeMonth(-1)} className="px-2 py-1 text-sm border rounded hover:bg-gray-50">◀</button>
          <span className="text-sm font-medium w-24 text-center">{yearMonth}</span>
          <button onClick={() => changeMonth(1)} className="px-2 py-1 text-sm border rounded hover:bg-gray-50">▶</button>
        </div>
      </div>

      <div className="p-4">
        {result && (
          <div
            className={`mb-3 px-3 py-2 rounded text-sm flex items-start justify-between gap-2 ${
              result.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
            }`}
          >
            <span>{result.text}</span>
            <button type="button" onClick={() => setResult(null)} className="text-xs opacity-60 hover:opacity-100">✕</button>
          </div>
        )}
        {loading ? (
          <div className="text-center py-6 text-gray-500">로딩 중...</div>
        ) : !data || data.sundays.length === 0 ? (
          <div className="text-center py-6 text-gray-400">{yearMonth}에 일요일이 없습니다.</div>
        ) : (
          <div className="space-y-2">
            {data.sundays.map(s => {
              const md = s.split('-').slice(1).join('/');
              const count = countOf(s);
              return (
                <div key={s} className="flex items-center justify-between border rounded-lg px-4 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">{md} (일)</span>
                    <button
                      type="button"
                      onClick={() => { if (count > 0) { setSearch(''); setModalSunday(s); } }}
                      disabled={count === 0}
                      className={`px-3 py-1 text-sm rounded-full font-medium ${
                        count > 0
                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer'
                          : 'bg-gray-100 text-gray-400 cursor-default'
                      }`}
                      title={count > 0 ? '클릭하여 출석 학생 명단 보기' : '출석 학생 없음'}
                    >
                      👥 출석 {count}명
                    </button>
                  </div>
                  {confirming === s ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600">부여할까요? (중복은 자동 방지)</span>
                      <button
                        onClick={() => handleManualAward(s)}
                        className="px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
                      >
                        확인
                      </button>
                      <button
                        onClick={() => setConfirming(null)}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        취소
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirming(s)}
                      disabled={awarding === s || count === 0}
                      className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50"
                    >
                      {awarding === s ? '부여 중...' : '⚡ 수동 부여'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="mt-3 text-xs text-gray-500">
          💡 자동 부여는 매주 월요일 아침 08:00에 실행됩니다. 그 전에 미리 부여하려면 ⚡ 수동 부여 버튼을 누르세요.
          <br />
          명수 칩(👥 출석 N명)을 누르면 그 일요일 출석 학생 명단이 팝업으로 열립니다. 중복 부여는 자동 방지·한도 초과분은 잘라서 부여됩니다.
        </p>
      </div>

      {/* 명단 팝업(모달) */}
      {modalSunday && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setModalSunday(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h4 className="font-semibold text-gray-900">
                {modalSunday.split('-').slice(1).join('/')} (일) 출석 학생
                <span className="ml-2 text-sm text-blue-700">{attendeesOf(modalSunday).length}명</span>
              </h4>
              <button
                type="button"
                onClick={() => setModalSunday(null)}
                className="text-gray-400 hover:text-gray-700 text-lg leading-none"
                aria-label="닫기"
              >
                ✕
              </button>
            </div>
            <div className="px-4 py-2 border-b">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="🔍 학생 이름 검색"
                className="w-full border rounded px-3 py-2 text-sm"
                autoFocus
              />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {modalAttendees.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  {search ? '검색 결과가 없습니다.' : '출석 학생이 없습니다.'}
                </div>
              ) : (
                <ul className="divide-y">
                  {modalAttendees.map(a => (
                    <li key={a.student_id} className="px-3 py-2 flex items-center justify-between hover:bg-gray-50">
                      <span className="font-medium text-gray-900">{a.name}</span>
                      <span className="text-sm text-gray-600">
                        <span className="font-semibold text-blue-700">{a.hours}시간</span>
                        <span className="ml-2 text-[11px] text-gray-500">
                          {fmtTime(a.first_entry)}~{fmtTime(a.last_exit)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="px-4 py-2 border-t flex justify-end">
              <button
                type="button"
                onClick={() => setModalSunday(null)}
                className="px-4 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SundayAttendanceView;
